import React from 'react'
import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from '../../../../../components/ModalSheet'
import JobProgressionBillingViewModel from './JobProgressionBillingViewModel'
import Button from 'components/Button'
import { FormGroupContainer, FormRow } from '../../../../../components/inputs'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from '../../../../../components/inputs/CustomSelect'
import { SelectItem } from '../../../../staffing/components/WorkspacePostingDialog/SingleItemSelector'
import { ExtensionPriceInputs } from '.'
import {
  Section,
  SectionContent,
  SectionHeader,
  useSection,
} from 'components/Section'
import { SelectedProgressionStep } from './declarations'

interface Props {
  vm: JobProgressionBillingViewModel
}

export default observer(({ vm }: Props) => {
  const progressionProps = useCustomSelectStateFromItemSource({
    itemSource: React.useMemo(
      () =>
        ItemSource.static(vm.availableProgressions, (item, text) =>
          item.name.toLowerCase().includes(text.toLowerCase()),
        ),
      [vm.availableProgressions],
    ),
  })

  const progressionStepsProps = useCustomSelectStateFromItemSource({
    itemSource: React.useMemo(
      () =>
        ItemSource.static(vm.availableSteps, (item, text) =>
          item.name.toLowerCase().includes(text.toLowerCase()),
        ),
      [vm.availableSteps],
    ),
  })

  return (
    <ModalSheet showing={vm.showing}>
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent leftAligned={true}>
        <ModalSheetSection horizontallySpaced>
          <ModalSheetContentTitle>
            Select job progression
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Add pricing to one or more job progression steps
          </ModalSheetContentDescription>

          <FormRow>
            <FormGroupContainer
              model={vm}
              field="selectedProgression"
              error={vm.validation.getError('selectedProgression')}
            >
              <CustomSelect
                {...progressionProps}
                multi={false}
                value={vm.selectedProgression}
                areItemsEqual={(left, right) => left?.value === right?.value}
                onChange={(p) => vm.setSelectedProgression(p!)}
                placeholder="Progression"
                itemToString={(i) => i.name}
                renderSearchItem={({ item, ...itemProps }) => (
                  <SelectItem key={item.name} {...itemProps}>
                    {item.name}
                  </SelectItem>
                )}
              />
            </FormGroupContainer>
          </FormRow>

          {vm.selectedProgression && (
            <>
              <FormRow>
                <FormGroupContainer
                  model={vm}
                  field="selectedSteps"
                  error={vm.validation.getError('selectedSteps')}
                >
                  <CustomSelect
                    {...progressionStepsProps}
                    multi={true}
                    value={vm.selectedStepsList}
                    areItemsEqual={(left, right) =>
                      left?.value === right?.value
                    }
                    onChange={(p) => vm.setSelectedSteps(p)}
                    placeholder="Steps"
                    itemToString={(i) => i.name}
                    renderSearchItem={({ item, ...progressionStepsProps }) => (
                      <SelectItem key={item.name} {...progressionStepsProps}>
                        {item.name}
                      </SelectItem>
                    )}
                  />
                </FormGroupContainer>
              </FormRow>
            </>
          )}
        </ModalSheetSection>

        {vm.selectedStepsList.map((selectedStep) => (
          <ProgressionStepSection
            key={selectedStep.value}
            selectedStep={selectedStep}
          />
        ))}
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Close
          </Button>
          <Button
            type="primary"
            data-test="manage-user-capacity-submit-button"
            onClick={vm.save}
          >
            Save
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

const ProgressionStepSection = observer(
  ({ selectedStep }: { selectedStep: SelectedProgressionStep }) => {
    const sectionProps = useSection(false)

    return (
      <Section {...sectionProps} key={selectedStep.value}>
        <SectionHeader>{selectedStep.name}</SectionHeader>

        <SectionContent>
          <div style={{ padding: '12px 32px' }}>
            <ExtensionPriceInputs vm={selectedStep.pricingVM} />
          </div>
        </SectionContent>
      </Section>
    )
  },
)
